import ErrorMessage from "@/v2-ui/ErrorMessage"
import Dialog, { type DialogProps } from "@/v2-ui/dialog"
import { toast } from "@/v2-ui/toast"
import { useCustomerCreateMutation } from "@/v2-backoffice-client/customer/create/customer.create.mutation"
import CustomerCreateForm from "@/v2-backoffice-client/customer/create/CustomerCreateForm"
import type { CustomerMatchCandidate } from "@/v2-backoffice-client/customer/create/CustomerMatchList"
import { getCreateOutcomeToast } from "@/v2-backoffice-client/customer/create/customer.create.utils"
import type { CustomerCreateMutation } from "@/v2-backoffice-client/customer/create/__types__/customer.create.mutation"

export type CreatedCustomer =
  CustomerCreateMutation["createCustomer"]["Customer"]

export type CustomerCreateDialogProps = DialogProps & {
  onCreated?(customer: CreatedCustomer): void
  /** The operator chose an existing customer from the matches — open it. */
  onOpenExisting?(candidate: CustomerMatchCandidate): void
}

function CustomerCreateDialog(props: CustomerCreateDialogProps) {
  const { open, onOpenChange, onCreated, onOpenExisting, ...dialogProps } =
    props

  const [createCustomer, { loading, error }] = useCustomerCreateMutation({
    // The board's paginated list + tab counts don't know about the new row;
    // evicting them makes the active queries refetch.
    update(cache) {
      cache.evict({ id: "ROOT_QUERY", fieldName: "getCustomers" })
      cache.evict({ id: "ROOT_QUERY", fieldName: "getCustomerCounts" })
      cache.gc()
    },
    onCompleted(data) {
      const { Customer, Crm } = data.createCustomer
      toast(getCreateOutcomeToast(Customer.Name ?? "The customer", Crm))
      onCreated?.(Customer)
      onOpenChange?.(false)
    },
  })

  return (
    <Dialog open={open} onOpenChange={onOpenChange} {...dialogProps}>
      <Dialog.Content align="top">
        <Dialog.Header>
          <Dialog.Title>Create New Customer</Dialog.Title>
          <Dialog.Description>
            Backoffice and HubSpot are checked as you type, so nothing gets
            created twice. New companies go to HubSpot too unless you say
            otherwise.
          </Dialog.Description>
        </Dialog.Header>

        <ErrorMessage className="my-4 text-center" error={error} />

        {/* Remounted per open so a reopened dialog starts clean. */}
        {open && (
          <CustomerCreateForm
            id="customer-create-form"
            loading={loading}
            onCancel={() => {
              onOpenChange?.(false)
            }}
            onOpenCustomer={(candidate) => {
              onOpenChange?.(false)
              onOpenExisting?.(candidate)
            }}
            onSubmit={(data) => {
              createCustomer({
                variables: { input: data.input, crm: data.crm },
              })
            }}
          />
        )}
      </Dialog.Content>
    </Dialog>
  )
}

export default CustomerCreateDialog
